import { useParams } from 'react-router-dom';
import { Alert, Stack } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getBuckets } from '@requests/buckets/buckets.requests';
import { Button } from '@common/Button';
import { BucketField } from './BucketField';
import { GetBucketsParams, TelnyxBucket } from '@requests/buckets';
import { RequestError } from '@requests/common';

interface Props {
  indexes: any[];
  addIndex: () => void;
  updateField: (indexField: 'index' | 'weight', indexFieldId: string, indexValue: string, indexType: string) => void;
  removeIndex: (value: string) => void;
  hideAddIndex?: boolean;
  allBuckets?: boolean;
}

export const BucketInput: React.FC<Props> = ({
  indexes,
  addIndex,
  updateField,
  removeIndex,
  hideAddIndex,
  allBuckets,
}) => {
  const { service_id } = useParams();

  const { data: buckets = [], isLoading } = useQuery<
    TelnyxBucket[],
    RequestError,
    TelnyxBucket[],
    [string, string, GetBucketsParams]
  >({
    queryKey: ['globalFiles', 'buckets', { service_id: service_id as string, all: Boolean(allBuckets) }],
    queryFn: getBuckets,
  });

  if (buckets.filter((item) => item.embedded).length === 0 && !isLoading) {
    return (
      <Stack spacing={2} display='flex' alignItems='start'>
        <Alert severity='warning'>
          You do not have any embedded buckets in this workspace. Embed a bucket to start searching.
        </Alert>
      </Stack>
    );
  }

  if (!indexes.length)
    return (
      <Button onClick={addIndex} startIcon={<Add />}>
        Add Bucket
      </Button>
    );

  return (
    <Stack spacing='1rem'>
      {indexes.map((idx) => (
        <BucketField
          key={idx.id}
          id={idx.id}
          removeField={removeIndex}
          setIndex={({ value, field, type }) => {
            const indexField = field;
            const indexFieldId = idx.id;
            const indexValue = value;
            const indexType = type;

            return updateField(indexField, indexFieldId, indexValue, indexType);
          }}
          index={idx.index}
          weight={idx.weight}
          indexes={indexes}
          buckets={buckets.filter((bucket) => bucket.embedded)}
          bucketsLoading={isLoading}
        />
      ))}
      {indexes.length < buckets.filter((item) => item.embedded).length && !hideAddIndex && (
        <Stack spacing={2} display='flex' alignItems='start'>
          <Button onClick={addIndex} startIcon={<Add />} tiny>
            Add Index
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
